<template>
  <div class="booking-seats">
    <!-- 檢視模式切換 -->
    <b-button-group v-if="showModeSelection" class="booking-seats__type-wrapper">
      <b-button
        variant="outline-primary"
        :class="{ active: selectedMode === 'capacity' }"
        @click="selectedMode = 'capacity'"
      >
        席次
      </b-button>
      <b-button
        variant="outline-primary"
        :class="{ active: selectedMode === 'staff' }"
        @click="selectedMode = 'staff'"
      >
        員工
      </b-button>
      <b-button
        variant="outline-primary"
        :class="{ active: selectedMode === 'service' }"
        @click="selectedMode = 'service'"
      >
        服務
      </b-button>
    </b-button-group>

    <!-- 席次檢視 -->
    <div v-if="selectedMode === 'capacity'">
      <!-- Header row -->
      <div
        class="booking-seats__grid-body"
        :style="{ '--slot-count': actualSlotCount }"
      >
        <div class="booking-seats__row booking-seats__row--header">
          <div class="booking-seats__time-label booking-seats__time-label--transparent"></div>
          <div
            v-for="index in actualSlotCount"
            :key="index"
            class="booking-seats__header-cell"
            :class="{ 'booking-seats__header-cell--exceeded': index > maximumBookingSlotCount }"
          >
            <template v-if="index <= maximumBookingSlotCount">
              席次 {{ index }}
            </template>
            <template v-else>
              超額 {{ index - maximumBookingSlotCount }}
            </template>
          </div>
        </div>
      </div>

      <!-- Data rows with overlay -->
      <div class="booking-seats__capacity-wrapper">
        <!-- Base layer for grid structure -->
        <div
          class="booking-seats__grid-body"
          :style="{ '--slot-count': actualSlotCount, ...(cellMinHeight && { '--cell-min-height': `${cellMinHeight}px` }) }"
        >
          <div
            v-for="(timePeriod, timePeriodIndex) in timePeriods"
            :key="timePeriod.start"
            class="booking-seats__row"
            :data-row-index="timePeriodIndex"
          >
            <div
              class="booking-seats__time-label"
              :class="{ 'booking-seats__time-label--full-border': timePeriodIsFull(timePeriod) }"
            >
              {{ timePeriodDisplayedText(timePeriod) }}
            </div>
            <div
              v-for="index in actualSlotCount"
              :key="`slot-${index}`"
              class="booking-seats__body-cell booking-seats__body-cell--placeholder"
              :class="{ 'booking-seats__body-cell--exceeded': index > maximumBookingSlotCount }"
              :data-column-index="index"
            >
            </div>
          </div>
        </div>

        <!-- Overlay layer for bookings -->
        <div
          class="booking-seats__overlay"
          :style="{ '--slot-count': actualSlotCount, ...(cellMinHeight && { '--cell-min-height': `${cellMinHeight}px` }) }"
        >
          <div
            v-for="booking in capacityViewBookings"
            :key="booking.id"
            class="booking-seats__overlay-cell"
            :style="{
              gridRow: booking.rowSpan > 1 ? `${booking.gridRow} / span ${booking.rowSpan}` : booking.gridRow,
              gridColumn: booking.gridColumn + 2
            }"
          >
            <div
              :id="`capacity-booking-${booking.id}`"
              class="booking-seats__booking-cell"
              tabindex="0"
            >
              {{ booking.title }}
            </div>
            <b-popover
              :target="`capacity-booking-${booking.id}`"
              triggers="focus"
              placement="top"
              custom-class="booking-detail-popover"
            >
              <div class="booking-detail">
                <div class="booking-detail__title">{{ booking.title }}</div>
                <div class="booking-detail__info">
                  <div class="booking-detail__row" v-if="booking.branchName">
                    <span class="booking-detail__label">分店：</span>
                    <span>{{ booking.branchName }}</span>
                  </div>
                  <div class="booking-detail__row" v-if="getStaffName(booking)">
                    <span class="booking-detail__label">員工：</span>
                    <span>{{ getStaffName(booking) }}</span>
                  </div>
                  <div class="booking-detail__row" v-if="getServiceName(booking)">
                    <span class="booking-detail__label">服務：</span>
                    <span>{{ getServiceName(booking) }}</span>
                  </div>
                  <div class="booking-detail__row">
                    <router-link
                      v-if="presetId"
                      :to="{
                        name: 'BookingCenterBookingEdit',
                        params: {
                          id: presetId,
                          booking_id: booking.id
                        }
                      }"
                      target="_blank"
                      class="booking-detail__link"
                    >
                      查看明細
                      <i class="mdi mdi-open-in-new ml-1"></i>
                    </router-link>
                    <span v-else class="booking-detail__link">
                      查看明細
                      <i class="mdi mdi-open-in-new ml-1"></i>
                    </span>
                  </div>
                </div>
              </div>
            </b-popover>
          </div>
        </div>
      </div>
    </div>

    <!-- 員工檢視 -->
    <div
      v-if="selectedMode === 'staff'"
      class="booking-seats__grid-body"
      :style="{ '--slot-count': staffs.length, ...(cellMinHeight && { '--cell-min-height': `${cellMinHeight}px` }) }"
    >
      <div class="booking-seats__row booking-seats__row--header">
        <div class="booking-seats__time-label booking-seats__time-label--transparent"></div>
        <div
          v-for="staff in staffs"
          :key="staff.id"
          class="booking-seats__header-cell"
        >
          {{ staff.name }}
        </div>
      </div>
      <div
        v-for="(timePeriod, timePeriodIndex) in timePeriods"
        :key="timePeriod.start"
        class="booking-seats__row"
      >
        <div
          class="booking-seats__time-label"
          :class="{ 'booking-seats__time-label--full': timePeriodIsFull(timePeriod) }"
        >
          {{ timePeriod.start }} - {{ timePeriod.end }}
        </div>
        <div
          v-for="(staff, staffIndex) in staffs"
          :key="staff.id"
          class="booking-seats__body-cell"
          :class="{ 'booking-seats__body-cell--empty': !getBookingForCell(timePeriod, staff) }"
          @click="!getBookingForCell(timePeriod, staff) && handleCellClick(timePeriod, staff)"
        >
          <div
            v-if="getBookingForCell(timePeriod, staff)"
            :id="`staff-booking-${timePeriodIndex}-${staffIndex}`"
            class="booking-seats__booking-cell"
            tabindex="0"
          >
            {{ getBookingForCell(timePeriod, staff).title }}
          </div>
          <b-popover
            v-if="getBookingForCell(timePeriod, staff)"
            :target="`staff-booking-${timePeriodIndex}-${staffIndex}`"
            triggers="focus"
            placement="top"
            custom-class="booking-detail-popover"
          >
            <div class="booking-detail">
              <div class="booking-detail__title">{{ getBookingForCell(timePeriod, staff).title }}</div>
              <div class="booking-detail__info">
                <div class="booking-detail__row" v-if="getBookingForCell(timePeriod, staff)?.branchName">
                  <span class="booking-detail__label">分店：</span>
                  <span>{{ getBookingForCell(timePeriod, staff)?.branchName }}</span>
                </div>
                <div class="booking-detail__row" v-if="staff.name">
                  <span class="booking-detail__label">員工：</span>
                  <span>{{ staff.name }}</span>
                </div>
                <div class="booking-detail__row" v-if="getServiceName(getBookingForCell(timePeriod, staff))">
                  <span class="booking-detail__label">服務：</span>
                  <span>{{ getServiceName(getBookingForCell(timePeriod, staff)) }}</span>
                </div>
                <div class="booking-detail__row">
                  <router-link
                    v-if="presetId"
                    :to="{
                      name: 'BookingCenterBookingEdit',
                      params: {
                        id: presetId,
                        booking_id: getBookingForCell(timePeriod, staff).id
                      }
                    }"
                    target="_blank"
                    class="booking-detail__link"
                  >
                    查看明細
                    <i class="mdi mdi-open-in-new ml-1"></i>
                  </router-link>
                  <span v-else class="booking-detail__link">
                    查看明細
                    <i class="mdi mdi-open-in-new ml-1"></i>
                  </span>
                </div>
              </div>
            </div>
          </b-popover>
        </div>
      </div>
    </div>

    <!-- 服務檢視 -->
    <div
      v-if="selectedMode === 'service'"
      class="booking-seats__grid-body"
      :style="{ '--slot-count': services.length, ...(cellMinHeight && { '--cell-min-height': `${cellMinHeight}px` }) }"
    >
      <div class="booking-seats__row booking-seats__row--header">
        <div class="booking-seats__time-label booking-seats__time-label--transparent"></div>
        <div
          v-for="service in services"
          :key="service.id"
          class="booking-seats__header-cell"
        >
          {{ service.name }}
        </div>
      </div>
      <div
        v-for="(timePeriod, timePeriodIndex) in timePeriods"
        :key="timePeriod.start"
        class="booking-seats__row"
      >
        <div class="booking-seats__time-label">
          {{ timePeriod.start }} - {{ timePeriod.end }}
        </div>
        <div
          v-for="service in services"
          :key="service.id"
          class="booking-seats__body-cell booking-seats__body-cell--service"
          :class="{
            'booking-seats__body-cell--full': isServiceTimePeriodFull(timePeriod, service) && timePeriodIndex % 2 === 0,
            'booking-seats__body-cell--full-border': isServiceTimePeriodFull(timePeriod, service) && timePeriodIndex % 2 === 1,
            'booking-seats__body-cell--empty': getBookingsForServiceCell(timePeriod, service).length === 0,
            'booking-seats__body-cell--clickable': !isServiceTimePeriodFull(timePeriod, service)
          }"
          @click="handleCellClick(timePeriod, null, service)"
        >
          <div class="booking-seats__service-bookings">
            <template v-for="(booking, bookingIndex) in getVisibleBookingsForServiceCell(timePeriod, service)">
              <div
                :key="booking.id"
                :id="`booking-${timePeriodIndex}-${bookingIndex}`"
                class="booking-seats__booking-row"
                tabindex="0"
                @click.stop
              >
                {{ booking.title }}
              </div>
              <b-popover
                :target="`booking-${timePeriodIndex}-${bookingIndex}`"
                triggers="focus"
                placement="top"
                custom-class="booking-detail-popover"
              >
                <div class="booking-detail">
                  <div class="booking-detail__title">{{ booking.title }}</div>
                  <div class="booking-detail__info">
                    <div class="booking-detail__row" v-if="booking.branchName">
                      <span class="booking-detail__label">分店：</span>
                      <span>{{ booking.branchName }}</span>
                    </div>
                    <div class="booking-detail__row" v-if="getStaffName(booking)">
                      <span class="booking-detail__label">員工：</span>
                      <span>{{ getStaffName(booking) }}</span>
                    </div>
                    <div class="booking-detail__row" v-if="getServiceName(booking)">
                      <span class="booking-detail__label">服務：</span>
                      <span>{{ getServiceName(booking) }}</span>
                    </div>
                    <div class="booking-detail__row">
                      <router-link
                        v-if="presetId"
                        :to="{
                          name: 'BookingCenterBookingEdit',
                          params: {
                            id: presetId,
                            booking_id: booking.id
                          }
                        }"
                        target="_blank"
                        class="booking-detail__link"
                      >
                        查看明細
                        <i class="mdi mdi-open-in-new ml-1"></i>
                      </router-link>
                      <span v-else class="booking-detail__link">
                        查看明細
                        <i class="mdi mdi-open-in-new ml-1"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </b-popover>
            </template>
            <template v-if="getHiddenBookingCount(timePeriod, service) > 0">
              <div
                :id="`popover-booking-list-${timePeriod.id}-${service.id}`"
                class="booking-seats__show-more"
                tabindex="0"
              >
                +{{ getHiddenBookingCount(timePeriod, service) }} 顯示更多
              </div>
              <b-popover
                :target="`popover-booking-list-${timePeriod.id}-${service.id}`"
                triggers="focus"
                placement="top"
                custom-class="booking-list-popover"
              >
                <div class="booking-list">
                  <div
                    v-for="booking in getBookingsForServiceCell(timePeriod, service)"
                    :key="booking.id"
                    class="booking-list__item"
                  >
                    <div class="booking-list__title">{{ booking.title }}</div>
                    <div class="booking-list__info">
                      <span class="booking-list__label">員工：</span>{{ getStaffName(booking) }}
                    </div>
                  </div>
                </div>
              </b-popover>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 選擇 Modal -->
    <b-modal
      v-model="showSelectionModal"
      title="新增預約"
      :ok-disabled="!bookingCreationIsValid"
      @ok="confirmSelection"
      @cancel="cancelSelection"
    >
      <b-form-group v-if="showStaffSelect" label="請選擇員工">
        <b-form-select
          v-model="selectedStaffId"
          :options="availableStaffOptions"
        >
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="showServiceSelect" label="請選擇服務">
        <b-form-select
          v-model="selectedServiceId"
          :options="availableServiceOptions"
        >
        </b-form-select>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'

const MAXIMUM_BOOKINGS_IN_CELL = 3

export default {
  name: 'BookingSeats',
  props: {
    timePeriods: {
      type: Array,
      required: true
    },
    staffs: {
      type: Array,
      required: true
    },
    services: {
      type: Array,
      required: true
    },
    bookings: {
      type: Array,
      required: true
    },
    maximumBookingSlotCount: {
      type: Number,
      default: 5
    },
    showModeSelection: {
      type: Boolean,
      default: true
    },
    enableBookingCreation: {
      type: Boolean,
      default: false
    },
    cellMinHeight: {
      type: Number,
      default: null
    },
    presetId: {
      type: [Number, String],
      required: false
    }
  },
  data() {
    return {
      selectedMode: 'capacity',
      showSelectionModal: false,
      selectedStaffId: null,
      selectedServiceId: null,
      pendingTimePeriod: null,
      pendingStaff: null,
      pendingService: null
    }
  },
  computed: {
    showStaffSelect() {
      return this.selectedMode === 'capacity' || this.selectedMode === 'service'
    },
    showServiceSelect() {
      return this.selectedMode === 'capacity' || this.selectedMode === 'staff'
    },
    bookingCreationIsValid() {
      return [
        this.showStaffSelect && !this.selectedStaffId,
        this.showServiceSelect && !this.selectedServiceId
      ].every(condition => false === condition)
    },
    availableStaffOptions() {
      if (!this.pendingTimePeriod) {
        return []
      }

      const availableStaffs = this.getAvailableStaffsForTimePeriod(this.pendingTimePeriod)
      return availableStaffs.map(staff => ({
        value: staff.id,
        text: staff.name
      }))
    },
    availableServiceOptions() {
      if (!this.pendingTimePeriod) {
        return []
      }

      const availableServices = this.getAvailableServicesForTimePeriod(this.pendingTimePeriod)
      return availableServices.map(service => ({
        value: service.id,
        text: service.name
      }))
    },
    actualSlotCount() {
      // Calculate the maximum number of bookings in any time period
      const maxBookingsInAnyPeriod = Math.max(
        ...this.timePeriods.map(tp => this.getBookingsForTimePeriod(tp).length),
        0
      )
      return Math.max(this.maximumBookingSlotCount, maxBookingsInAnyPeriod)
    },
    capacityViewBookings() {
      const bookingsWithPosition = []
      const processedBookingIds = new Set()

      // Track which columns are occupied in each row
      const columnOccupancy = this.timePeriods.map(() => new Set())

      this.timePeriods.forEach((timePeriod, timePeriodIndex) => {
        const bookingsInPeriod = this.getBookingsForTimePeriod(timePeriod)

        bookingsInPeriod.forEach(booking => {
          // Skip if already processed (for multi-period bookings)
          if (processedBookingIds.has(booking.id)) return

          // Calculate row span
          const rowSpan = this.calculateBookingRowSpan(booking, timePeriodIndex)

          // Find available column
          let gridColumn = 0
          while (columnOccupancy[timePeriodIndex].has(gridColumn)) {
            gridColumn++
          }

          // Mark columns as occupied for all spanned rows
          for (let i = 0; i < rowSpan; i++) {
            if (timePeriodIndex + i < columnOccupancy.length) {
              columnOccupancy[timePeriodIndex + i].add(gridColumn)
            }
          }

          bookingsWithPosition.push({
            ...booking,
            gridRow: timePeriodIndex + 1, // +1 because grid rows are 1-indexed
            gridColumn,
            rowSpan
          })

          processedBookingIds.add(booking.id)
        })
      })

      return bookingsWithPosition
    }
  },
  methods: {
    calculateBookingRowSpan(booking, startTimePeriodIndex) {
      // For legacy bookings with timePeriodId, no spanning
      if (booking.timePeriodId) return 1
      if (!booking.startAt || !booking.endAt) return 1

      let rowSpan = 0
      for (let i = startTimePeriodIndex; i < this.timePeriods.length; i++) {
        if (this.bookingOverlapsTimePeriod(booking, this.timePeriods[i])) {
          rowSpan++
        } else if (rowSpan > 0) {
          // Stop counting if we've started counting and hit a non-overlapping period
          break
        }
      }

      return Math.max(1, rowSpan)
    },
    bookingOverlapsTimePeriod(booking, timePeriod) {
      if (!booking.startAt || !booking.endAt) return false

      // Handle both full datetime strings and time-only strings
      const bookingStartTime = booking.startAt.includes(':') && !booking.startAt.includes(' ')
        ? booking.startAt
        : moment(booking.startAt).format('HH:mm')
      const bookingEndTime = booking.endAt.includes(':') && !booking.endAt.includes(' ')
        ? booking.endAt
        : moment(booking.endAt).format('HH:mm')

      return bookingStartTime < timePeriod.end && bookingEndTime > timePeriod.start
    },
    getBookingsForTimePeriod(timePeriod) {
      return this.bookings.filter(booking => {
        if (booking.timePeriodId) {
          return booking.timePeriodId === timePeriod.id
        }

        return this.bookingOverlapsTimePeriod(booking, timePeriod)
      })
    },
    getEmptySlotCount(timePeriod) {
      const bookingCount = this.getBookingsForTimePeriod(timePeriod).length
      return Math.max(0, this.maximumBookingSlotCount - bookingCount)
    },
    timePeriodIsFull(timePeriod) {
      return this.getBookingsForTimePeriod(timePeriod).length >= this.maximumBookingSlotCount
    },
    getBookingForCell(timePeriod, staff) {
      return this.bookings.find(booking => {
        if (booking.timePeriodId) {
          return booking.timePeriodId === timePeriod.id && booking.staffId === staff.id
        }

        if (booking.staffId !== staff.id) return false

        return this.bookingOverlapsTimePeriod(booking, timePeriod)
      })
    },
    getBookingsForServiceCell(timePeriod, service) {
      return this.bookings.filter(booking => {
        if (booking.timePeriodId) {
          return booking.timePeriodId === timePeriod.id && booking.serviceId === service.id
        }

        if (booking.serviceId !== service.id) return false

        return this.bookingOverlapsTimePeriod(booking, timePeriod)
      })
    },
    getVisibleBookingsForServiceCell(timePeriod, service) {
      const bookings = this.getBookingsForServiceCell(timePeriod, service)
      return bookings.slice(0, MAXIMUM_BOOKINGS_IN_CELL)
    },
    getHiddenBookingCount(timePeriod, service) {
      const bookings = this.getBookingsForServiceCell(timePeriod, service)
      return Math.max(0, bookings.length - MAXIMUM_BOOKINGS_IN_CELL)
    },
    isServiceTimePeriodFull(timePeriod, service) {
      const bookingCount = this.getBookingsForServiceCell(timePeriod, service).length
      return bookingCount >= service.maximun
    },
    timePeriodDisplayedText(timePeriod) {
      if (null === timePeriod.end) {
        return timePeriod.start
      }

      return `${timePeriod.start} - ${timePeriod.end}`
    },
    getStaffName(booking) {
      return booking.staffName
        || _.get(this.staffs.find(s => s.id === booking?.staffId), 'name', '')
    },
    getServiceName(booking) {
      return booking.serviceName
        || _.get(this.services.find(s => s.id === booking?.serviceId), 'name', '')
    },
    getAvailableStaffsForTimePeriod(timePeriod) {
      const bookedStaffIds = this.bookings
        .filter(booking => booking.timePeriodId === timePeriod.id)
        .map(booking => booking.staffId)

      return this.staffs.filter(staff => !bookedStaffIds.includes(staff.id))
    },
    getAvailableServicesForTimePeriod(timePeriod) {
      return this.services.filter(service => {
        const bookingCount = this.getBookingsForServiceCell(timePeriod, service).length
        return bookingCount < service.maximun
      })
    },
    handleCellClick(timePeriod, staff = null, service = null) {
      // Check if booking creation is enabled
      if (!this.enableBookingCreation) {
        return
      }

      // Check if cell is full
      if (this.selectedMode === 'capacity' && this.timePeriodIsFull(timePeriod)) {
        return
      }
      if (this.selectedMode === 'service' && service && this.isServiceTimePeriodFull(timePeriod, service)) {
        return
      }

      // Check if there are available options
      const availableStaffs = this.getAvailableStaffsForTimePeriod(timePeriod)
      const availableServices = this.getAvailableServicesForTimePeriod(timePeriod)

      if (this.showStaffSelect && availableStaffs.length === 0) {
        alert('此時段沒有可用的員工')
        return
      }
      if (this.showServiceSelect && availableServices.length === 0) {
        alert('此時段沒有可用的服務')
        return
      }

      // Set pending data
      this.pendingTimePeriod = timePeriod
      this.pendingStaff = staff
      this.pendingService = service

      // Pre-fill selections
      if (staff) {
        this.selectedStaffId = staff.id
      } else if (availableStaffs.length > 0) {
        this.selectedStaffId = availableStaffs[0].id
      }

      if (service) {
        this.selectedServiceId = service.id
      } else if (availableServices.length > 0) {
        this.selectedServiceId = availableServices[0].id
      }

      this.showSelectionModal = true
    },
    confirmSelection() {
      if (!this.bookingCreationIsValid || !this.pendingTimePeriod) {
        return
      }

      const staff = this.pendingStaff || this.staffs.find(s => s.id === this.selectedStaffId)
      const service = this.pendingService || this.services.find(s => s.id === this.selectedServiceId)

      this.$emit('create-booking', {
        timePeriod: this.pendingTimePeriod,
        staff,
        service
      })

      this.cancelSelection()
    },
    cancelSelection() {
      this.showSelectionModal = false
      this.selectedStaffId = null
      this.selectedServiceId = null
      this.pendingTimePeriod = null
      this.pendingStaff = null
      this.pendingService = null
    }
  }
}
</script>

<style scoped lang="scss">
$blue: #3788d8;
$blue-darker: #0056b3;
$full-cell-border-color: #ea707c;
$time-period-cell-width: 120px;

.booking-seats__type-wrapper {
  margin-bottom: 20px;
}

.booking-seats__capacity-wrapper {
  position: relative;
}

.booking-seats__grid-body {
  display: grid;
  grid-template-columns: $time-period-cell-width repeat(var(--slot-count, 4), 1fr);
  gap: 8px;
}

.booking-seats__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
  display: grid;
  grid-template-columns: $time-period-cell-width repeat(var(--slot-count, 4), 1fr);
  grid-auto-rows: var(--cell-min-height);
  gap: 8px;
}

.booking-seats__overlay-cell {
  pointer-events: auto;
  background-color: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.booking-seats__row {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.booking-seats__row--header {
  margin-bottom: 8px;
}

.booking-seats__time-label {
  background-color: #f8f9fa;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 8px;

  &--full-border {
    position: relative;
    outline: 2px solid $full-cell-border-color;

    &::after {
      content: '已滿';
      position: absolute;
      right: 2px;
      bottom: 0;
      font-size: 0.875rem;
      color: $full-cell-border-color;
    }
  }

  &--transparent {
    background-color: transparent;
  }
}

.booking-seats__header-cell {
  background-color: #d8d8d8;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 8px;

  &--exceeded {
    background-color: #f8d7da;
    color: #721c24;
  }
}

.booking-seats__body-cell {
  background-color: #f8f9fa;
  height: var(--cell-min-height);
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;

  &--placeholder {
    background-color: transparent;
    border: 1px dashed #dee2e6;

    &.booking-seats__body-cell--exceeded {
      border-color: #f5c6cb;
      background-color: #fff5f5;
    }
  }

  &--empty {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #e2e6ea;
    }
  }

  &--service {
    padding: 8px;
    overflow: visible;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      background-color: #e2e6ea;
    }
  }

  &--full {
    background-color: #ffb6bd;
  }

  &--full-border {
    position: relative;
    outline: 2px solid $full-cell-border-color;

    &::after {
      content: '已滿';
      position: absolute;
      right: 2px;
      bottom: 0;
      font-size: 0.875rem;
      color: $full-cell-border-color;
    }
  }
}

.booking-seats__booking-cell {
  background-color: $blue;
  color: white;
  padding: 12px;
  font-size: 0.875rem;
  flex: 1;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: $blue-darker;
  }
}

.booking-seats__service-bookings {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.booking-seats__booking-row {
  display: block;
  background-color: $blue;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: $blue-darker;
  }
}

.booking-seats__show-more {
  color: $blue;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    color: $blue-darker;
  }
}

::v-deep.booking-detail-popover {
  .popover-body {
    padding: 0;
  }

  .booking-detail {
    min-width: 200px;

    &__title {
      border-bottom: 1px solid #e9ecef;
      padding: 0.5rem 0.75rem;
      background-color: #f7f7f7;
      font-weight: 600;
      font-size: 0.875rem;
    }

    &__info {
      padding: 12px 16px;
    }

    &__row {
      margin-bottom: 8px;
      font-size: 0.875rem;
      line-height: 1.5;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__label {
      color: #6c757d;
    }

    &__link {
      color: $blue;
      cursor: pointer;
    }
  }
}

::v-deep.booking-list-popover {
  .popover-body {
    padding: 8px 0;
  }

  .booking-list {
    min-width: 180px;
    max-height: 300px;
    overflow-y: auto;

    &__item {
      padding: 8px 16px;
      border-bottom: 1px solid #e9ecef;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #f8f9fa;
      }
    }

    &__title {
      font-weight: 600;
      font-size: 0.875rem;
      margin-bottom: 4px;
    }

    &__info {
      font-size: 0.75rem;
      color: #6c757d;
    }

    &__label {
      font-weight: 500;
    }
  }
}
</style>
